import { CATEGORY_KEYS } from "@wallyo/contracts/categories";
import type { ConsolePlan, DiscountScope } from "@wallyo/contracts/console/billing";
import type { WorkerCategory } from "@wallyo/contracts/types";
import type { FormEvent } from "react";
import { t } from "../../i18n";
import { tradeName } from "../customers/customer-format";
import { PersonPicker } from "../features/PersonPicker";
import { Modal, WriteFailureNotice } from "../team/TeamDialogs";
import type { WriteFailure } from "../team/team-format";
import { NAME_MAX, type BillingFields, type BillingTarget } from "./billing-actions";

export type BillingDialogProps = {
  target: BillingTarget;
  /** The plans the price select offers, as the API listed them. */
  plans: readonly ConsolePlan[];
  fields: BillingFields;
  problem: string | null;
  failure: WriteFailure | null;
  pending: boolean;
  /** Picking a pro reads their account, which needs VIEW_ACCOUNTS. */
  canViewAccounts: boolean;
  onFields: (fields: Partial<BillingFields>) => void;
  onSubmit: () => void;
  onClose: () => void;
  onReload: () => void;
};

const SCOPES: readonly DiscountScope[] = ["pro", "country", "category"];

/** One billing write: what it does, its fields, the reason, then send. */
export function BillingDialog(props: BillingDialogProps) {
  const { target, fields } = props;
  const kind = target.kind;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSubmit();
  };
  return (
    <Modal title={t(`billing.dialog.${kind}.title` as const, { subject: subjectOf(target) })} onClose={props.onClose}>
      <form className="modal-form" onSubmit={submit} noValidate data-dialog={kind}>
        <div className="modal-body">
          <p>{t(`billing.dialog.${kind}.body` as const)}</p>
        </div>
        <PriceFields {...props} />
        <PromoFields {...props} />
        <DiscountFields {...props} />
        <ExtendFields {...props} />
        <div className="field">
          <label htmlFor="billing-reason">{t("team.reason")}</label>
          <textarea
            id="billing-reason"
            rows={2}
            value={fields.reason}
            onChange={(event) => props.onFields({ reason: event.target.value })}
          />
          <span className="meta">{t("team.reasonHint")}</span>
        </div>
        <Problem problem={props.problem} />
        <Failure failure={props.failure} onReload={props.onReload} />
        <div className="btns modal-actions">
          <button className="btn quiet" type="button" onClick={props.onClose}>
            {t("team.cancel")}
          </button>
          <SubmitButton kind={kind} pending={props.pending} />
        </div>
      </form>
    </Modal>
  );
}

/** Who or what the title names: the promo, the remise, or the pro's plan. */
function subjectOf(target: BillingTarget): string {
  switch (target.kind) {
    case "endPromo":
      return target.promo.name;
    case "endDiscount":
      return target.discount.name;
    case "extendSubscription":
    case "endSubscription":
      return target.subscription.planName;
    default:
      return "";
  }
}

function SubmitButton({ kind, pending }: { kind: BillingTarget["kind"]; pending: boolean }) {
  let tone = "primary";
  if (kind === "endPromo" || kind === "endDiscount" || kind === "endSubscription") tone = "danger";
  if (pending) {
    return (
      <button className={`btn ${tone}`} type="submit" disabled>
        {t("team.sending")}
      </button>
    );
  }
  return (
    <button className={`btn ${tone}`} type="submit">
      {t(`billing.action.${kind}` as const)}
    </button>
  );
}

function Problem({ problem }: { problem: string | null }) {
  if (!problem) return null;
  return <p className="field-problem">{problem}</p>;
}

function Failure({ failure, onReload }: { failure: WriteFailure | null; onReload: () => void }) {
  if (!failure) return null;
  return <WriteFailureNotice failure={failure} onReload={onReload} />;
}

// ── The fields of each write ──────────────────────────────────────────────

function PlanSelect({
  plans,
  value,
  withAnyPlan,
  onPick,
}: {
  plans: readonly ConsolePlan[];
  value: string;
  /** The remise form offers "any plan"; a price and a promo name one. */
  withAnyPlan: boolean;
  onPick: (feeTierId: string) => void;
}) {
  return (
    <div className="field">
      <label htmlFor="billing-plan">{t("billing.field.plan")}</label>
      <select id="billing-plan" value={value} onChange={(event) => onPick(event.target.value)}>
        <AnyPlanOption show={withAnyPlan} />
        <PickPlanOption show={!withAnyPlan} />
        {plans.map((plan) => (
          <option key={plan.feeTierId} value={plan.feeTierId}>
            {plan.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function AnyPlanOption({ show }: { show: boolean }) {
  if (!show) return null;
  return <option value="">{t("billing.anyPlan")}</option>;
}

function PickPlanOption({ show }: { show: boolean }) {
  if (!show) return null;
  return <option value="">{t("billing.field.pickPlan")}</option>;
}

function CountryField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="field">
      <label htmlFor="billing-country">{t("billing.field.country")}</label>
      <input
        id="billing-country"
        className="mono"
        maxLength={2}
        value={value}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
      />
      <span className="meta">{t("billing.field.countryHint")}</span>
    </div>
  );
}

function AmountField({
  currency,
  value,
  onChange,
}: {
  currency: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label htmlFor="billing-amount">{t("billing.field.amount", { currency })}</label>
      <input id="billing-amount" inputMode="decimal" value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function WindowFields({ fields, onFields }: { fields: BillingFields; onFields: BillingDialogProps["onFields"] }) {
  return (
    <>
      <div className="field">
        <label htmlFor="billing-starts">{t("billing.field.startsAt")}</label>
        <input
          id="billing-starts"
          type="datetime-local"
          value={fields.startsAt}
          onChange={(event) => onFields({ startsAt: event.target.value })}
        />
      </div>
      <div className="field">
        <label htmlFor="billing-ends">{t("billing.field.endsAt")}</label>
        <input
          id="billing-ends"
          type="datetime-local"
          value={fields.endsAt}
          onChange={(event) => onFields({ endsAt: event.target.value })}
        />
      </div>
      <div className="field">
        <label htmlFor="billing-periods">{t("billing.field.periods")}</label>
        <input
          id="billing-periods"
          inputMode="numeric"
          value={fields.periods}
          onChange={(event) => onFields({ periods: event.target.value })}
        />
        <span className="meta">{t("billing.field.periodsHint")}</span>
      </div>
    </>
  );
}

function NameField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="field">
      <label htmlFor="billing-name">{t("billing.field.name")}</label>
      <input id="billing-name" maxLength={NAME_MAX} value={value} onChange={(event) => onChange(event.target.value)} />
      <span className="meta">{t("billing.field.nameHint")}</span>
    </div>
  );
}

function PriceFields(props: BillingDialogProps) {
  if (props.target.kind !== "setPrice") return null;
  const { fields, onFields } = props;
  return (
    <>
      <PlanSelect
        plans={props.plans}
        value={fields.feeTierId}
        withAnyPlan={false}
        onPick={(feeTierId) => onFields({ feeTierId })}
      />
      <CountryField value={fields.country} onChange={(country) => onFields({ country })} />
      <div className="field">
        <label htmlFor="billing-currency">{t("billing.field.currency")}</label>
        <input
          id="billing-currency"
          className="mono"
          maxLength={3}
          value={fields.currency}
          onChange={(event) => onFields({ currency: event.target.value.toUpperCase() })}
        />
        <span className="meta">{t("billing.field.currencyHint")}</span>
      </div>
      <AmountField
        currency={fields.currency || "GNF"}
        value={fields.amount}
        onChange={(amount) => onFields({ amount })}
      />
      <div className="field">
        <label htmlFor="billing-valid-from">{t("billing.field.validFrom")}</label>
        <input
          id="billing-valid-from"
          type="datetime-local"
          value={fields.validFrom}
          onChange={(event) => onFields({ validFrom: event.target.value })}
        />
        <span className="meta">{t("billing.field.validFromHint")}</span>
      </div>
    </>
  );
}

function PromoFields(props: BillingDialogProps) {
  if (props.target.kind !== "newPromo") return null;
  const { fields, onFields } = props;
  return (
    <>
      <NameField value={fields.name} onChange={(name) => onFields({ name })} />
      <PlanSelect
        plans={props.plans}
        value={fields.feeTierId}
        withAnyPlan={false}
        onPick={(feeTierId) => onFields({ feeTierId })}
      />
      <CountryField value={fields.country} onChange={(country) => onFields({ country })} />
      <AmountField
        currency={fields.currency || "GNF"}
        value={fields.amount}
        onChange={(amount) => onFields({ amount })}
      />
      <WindowFields fields={fields} onFields={onFields} />
    </>
  );
}

function DiscountFields(props: BillingDialogProps) {
  if (props.target.kind !== "newDiscount") return null;
  const { fields, onFields } = props;
  return (
    <>
      <NameField value={fields.name} onChange={(name) => onFields({ name })} />
      <fieldset className="pick-list">
        <legend>{t("billing.field.scope")}</legend>
        {SCOPES.map((scope) => (
          <label key={scope}>
            <input
              type="radio"
              name="billing-scope"
              checked={fields.scope === scope}
              onChange={() => onFields({ scope, workerId: "", country: "", category: "" })}
            />{" "}
            {t(`billing.scope.${scope}` as const)}
          </label>
        ))}
      </fieldset>
      <ScopeTarget {...props} />
      <PlanSelect
        plans={props.plans}
        value={fields.feeTierId}
        withAnyPlan
        onPick={(feeTierId) => onFields({ feeTierId })}
      />
      <div className="field">
        <label htmlFor="billing-percent">{t("billing.field.percentOff")}</label>
        <input
          id="billing-percent"
          inputMode="numeric"
          value={fields.percentOff}
          onChange={(event) => onFields({ percentOff: event.target.value })}
        />
      </div>
      <WindowFields fields={fields} onFields={onFields} />
    </>
  );
}

/** Exactly one target field, the one the chosen scope needs. */
function ScopeTarget(props: BillingDialogProps) {
  const { fields, onFields } = props;
  if (fields.scope === "country") {
    return <CountryField value={fields.country} onChange={(country) => onFields({ country })} />;
  }
  if (fields.scope === "category") {
    return (
      <div className="field">
        <label htmlFor="billing-category">{t("billing.field.category")}</label>
        <select
          id="billing-category"
          value={fields.category}
          onChange={(event) => onFields({ category: event.target.value })}
        >
          <option value="">{t("billing.field.pickCategory")}</option>
          {sortedCategories().map((category) => (
            <option key={category} value={category}>
              {tradeName(category)}
            </option>
          ))}
        </select>
      </div>
    );
  }
  return <ProField {...props} />;
}

function sortedCategories(): WorkerCategory[] {
  return [...CATEGORY_KEYS].sort((left, right) => tradeName(left).localeCompare(tradeName(right)));
}

/** The pro a remise is for: already filled in when arriving from their page. */
function ProField({ fields, canViewAccounts, onFields }: BillingDialogProps) {
  if (fields.workerId.trim()) {
    return (
      <div className="field">
        <span>{t("billing.field.pro")}</span>
        <span className="picked">
          <b className="mono">{fields.workerId}</b>
          <button className="btn quiet small" type="button" onClick={() => onFields({ workerId: "" })}>
            {t("features.form.pickedChange")}
          </button>
        </span>
      </div>
    );
  }
  return (
    <div className="field">
      <span>{t("billing.field.pro")}</span>
      <PersonPicker
        pro
        canView={canViewAccounts}
        picked={null}
        onPick={(person) => onFields({ workerId: person?.proProfileId ?? "" })}
      />
    </div>
  );
}

function ExtendFields(props: BillingDialogProps) {
  if (props.target.kind !== "extendSubscription") return null;
  const { fields, onFields } = props;
  return (
    <div className="field">
      <label htmlFor="billing-paid-through">{t("billing.field.paidThrough")}</label>
      <input
        id="billing-paid-through"
        type="datetime-local"
        value={fields.paidThrough}
        onChange={(event) => onFields({ paidThrough: event.target.value })}
      />
      <span className="meta">{t("billing.field.paidThroughHint")}</span>
    </div>
  );
}
